import { queryOptions } from '@tanstack/react-query'
import { fetchCardManagementCapabilities } from 'src/queries/physicalCardManagement/physicalCardManagement.network'

const CAPABILITIES_STALE_TIME_MS = 30_000

// Self-contained keys live next to the factory; nothing cross-feature invalidates
// this yet, so they stay out of queries.constants.ts.
//
// A named family prefix + an exact-key factory, deliberately no domain-wide
// `all()`: once the full CapabilityResolver lands and freeze / MCC / limits
// become their own families under this domain, a shared root would let one
// `invalidateQueries` refetch every family with the fan-out invisible at the
// call site. Each family gets its own prefix instead.
export const physicalCardManagementQueryKeys = {
  capabilitiesAll: () => ['physicalCardManagement', 'capabilities'] as const,
  capabilities: (
    organizationId: string | undefined,
    creditCardId: string | undefined,
  ) =>
    [
      ...physicalCardManagementQueryKeys.capabilitiesAll(),
      organizationId,
      creditCardId,
    ] as const,
}

// Guard the required args inside the queryFn, not only via `enabled`: a spread
// override replaces `enabled` rather than merging it, and direct cache readers
// (prefetchQuery / ensureQueryData) bypass the hook entirely — either path would
// otherwise build a malformed URL and fire it.
const getCapabilities = (
  organizationId: string | undefined,
  creditCardId: string | undefined,
) => {
  if (!organizationId || !creditCardId) {
    throw new Error(
      'Card Management capabilities require an organizationId and a creditCardId',
    )
  }

  return fetchCardManagementCapabilities(organizationId, creditCardId)
}

export const physicalCardManagementQueries = {
  capabilities: (
    organizationId: string | undefined,
    creditCardId: string | undefined,
  ) =>
    queryOptions({
      queryKey: physicalCardManagementQueryKeys.capabilities(
        organizationId,
        creditCardId,
      ),
      queryFn: () => getCapabilities(organizationId, creditCardId),
      // Match the queryFn's truthiness guard exactly: gating on `!== undefined`
      // would let an empty-string id enable the query, which then throws in
      // `getCapabilities` and drops straight into an error state.
      enabled: Boolean(organizationId) && Boolean(creditCardId),
      // A denied/absent capability is a real answer, not a transient failure —
      // retrying would just delay the fail-closed result.
      retry: false,
      // The gate wraps the card-detail surface, so the default staleTime of 0
      // costs a request per navigation and per window focus. This decision
      // folds an env kill-switch and per-card enrollment — neither is volatile
      // by the second — so a short window trades a bounded delay on a
      // kill-switch flip for a large drop in traffic. Visibility only: every
      // real card-management action is authorized server-side on its own.
      staleTime: CAPABILITIES_STALE_TIME_MS,
    }),
}
